import { Button } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Base, CardTitle } from "@/atoms-react/text/Text.react";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { useUser } from "@/features/user/hooks/use-user";
import type { AddressFullType } from "@/features/user/types";
import { useLocale } from "@/features/utils/hooks/use-locale";
import { Themes } from "@/types/theme";
import { TrashIcon } from "@heroicons/react/20/solid";
import { HomeIcon, MapPinIcon, PencilSquareIcon, ShieldCheckIcon } from "@heroicons/react/24/solid";
import type React from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
type AddressCardProps = {
  address: AddressFullType;
  onEdit?: () => void;
  onDelete?: () => void;
  olkypassLevel?: number;
  disabled?: boolean;
} & React.ComponentPropsWithoutRef<"div">;

export const AddressCard = ({
  address,
  onEdit = () => {},
  onDelete,
  olkypassLevel = -1,
  disabled = false,
  ...props
}: AddressCardProps) => {
  const { t } = useTranslation(["pass"]);
  const { locale } = useLocale();
  const { useCountries } = useUser();
  const { data: countries } = useCountries(locale?.language);
  const { leUuid } = useCompanyContext();

  const dataTooltip = disabled
    ? leUuid
      ? t("aliasAddressPage.tooltip.update_address")
      : t("aliasAddressPage.tooltip.update_main_address")
    : null;

  return (
    <Column {...props}>
      {/* Add min height to the card */}
      <div className="h-64 rounded-sm border-2 border-slate-50 px-6 py-4 dark:border-slate-700 dark:border-b-2">
        {/* First row of the card */}
        <div className="mb-6 flex items-center">
          <div className="mr-3 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border-2 border-primary">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary">
              {address.isPrincipalAddress ? (
                <HomeIcon className="h-6 w-6 text-white" />
              ) : (
                <MapPinIcon className="h-6 w-6 text-white" />
              )}{" "}
            </div>
          </div>
          <div className="flex w-28 flex-1 flex-wrap gap-x-3">
            <CardTitle className="mr-1 truncate text-base">{address.Tag}</CardTitle>
            {/**false && <Tag>{t('aliasAddressPage.tag')}</Tag>**/}
          </div>
          <div data-tooltip={t("olkypass.general.coming_soon")}>
            <Button onClick={() => null} size="md" icon={<ShieldCheckIcon />} disabled={true}>
              <span className="hidden md:inline">{t("aliasAddressPage.button.check")}</span>
            </Button>
          </div>
        </div>
        {/* Adress infos */}
        <div className="mb-6">
          <Base className="truncate">{address.Address1.replace(",", "")}</Base>
          <Base className="truncate">
            {address.City} {address.ZipCode}
          </Base>
          <Base>
            {countries?.countries && countries?.countries.length > 0 ? (
              (countries?.countries ?? []).find((c) => c.id.toString() === address.Country)
                ?.defaultName
            ) : (
              <Loader />
            )}
          </Base>
        </div>

        {/* If adress is not checked */}
        {/**false && (
          <Base className="text-sm text-slate-200">{t('aliasAddressPage.address_text')}</Base>
        )**/}
        {/* If adress is checked */}

        <div className="flex justify-end gap-x-2 md:mb-2">
          {onDelete && (
            <Button
              {...(dataTooltip && { dataTooltip })}
              dataCy={`button-delete-address-${address.Address1}-${address.City}-${address.Tag}`}
              contentProps={{ className: "space-x-0 md:space-x-2" }}
              onClick={() => {
                if (!address.isPrincipalAddress) {
                  onDelete();
                } else {
                  toast.error(t("aliasAddressPage.forbidden_remove_helper"));
                }
              }}
              size="md"
              className="w-max"
              theme={Themes.danger}
              icon={<TrashIcon />}
              disabled={disabled}
            >
              <span className="hidden md:inline">{t("aliasAddressPage.button.delete")}</span>
            </Button>
          )}
          <Button
            {...(dataTooltip && { dataTooltip })}
            dataCy={`button-edit-address-${address.Address1}-${address.City}-${address.Tag}`}
            contentProps={{ className: "space-x-0 md:space-x-2" }}
            onClick={onEdit}
            size="md"
            className="w-auto"
            theme={Themes.white}
            icon={<PencilSquareIcon />}
            disabled={disabled}
          >
            <span className="hidden md:inline">{t("aliasAddressPage.button.edit")}</span>
          </Button>
        </div>
        {/* If adress beign checked */}
        {/**<Tag theme={Themes.warning}>{t('aliasAddressPage.address_checking')}</Tag> */}
      </div>
    </Column>
  );
};
